// WbTicketStartModal — Phase C10 Group 1.
//
// Captures the four fields the Start modal owns BEFORE any /wb/tickets POST
// can happen — direction, weighing_mode, yard, optional weighbridge. The
// caller stages the result on WbTicketContext.pendingShell; the actual POST
// fires from the left rail (Group 2) once the customer field lands.
//
// We deliberately don't call createTicket() here: POST /wb/tickets requires
// customer_enc_id (see WEIGHBRIDGE_SERVER_CONTRACT.md §C10), and the
// customer picker lives in the left rail. Pinning customer + the four
// staged shell fields is what completes the required-field set.

import { useEffect, useState } from 'react';
import { Modal } from '../../../../components/ui/Modal/Modal';
import { Button } from '../../../../components/ui/Button/Button';
import { Spinner } from '../../../../components/ui/Spinner/Spinner';
import { cn } from '../../../../utils/cn';
import { useWbYards } from '../../hooks/useWbYards';
import { useWbWeighbridges } from '../../hooks/useWbWeighbridges';
import type { PendingTicketShell } from '../../context/WbTicketContext';
import type {
  TicketDirection,
  WeighingMode,
} from '../../hooks/useWbTicket';
import styles from './WbTicketStartModal.module.css';

export interface WbTicketStartModalProps {
  open: boolean;
  /** Caller dismisses + navigates back to the tickets list. */
  onCancel: () => void;
  /** Caller writes setPendingShell(shell) and closes the modal. */
  onStart: (shell: PendingTicketShell) => void;
}

interface DirectionOption {
  value: TicketDirection;
  title: string;
  hint: string;
}

const DIRECTION_OPTIONS: ReadonlyArray<DirectionOption> = [
  { value: 'inwards', title: 'Inwards', hint: 'Material arriving at the yard' },
  { value: 'outwards', title: 'Outwards', hint: 'Material leaving the yard' },
];

interface ModeOption {
  value: WeighingMode;
  title: string;
  hint: string;
}

const MODE_OPTIONS: ReadonlyArray<ModeOption> = [
  {
    value: 'two_pass',
    title: 'Two-pass',
    hint: 'Weigh in, then weigh out',
  },
  {
    value: 'stored_tare',
    title: 'Stored tare',
    hint: 'Use the vehicle’s stored tare',
  },
  {
    value: 'manual_tare',
    title: 'Manual tare',
    hint: 'Enter tare weight manually',
  },
  {
    value: 'units_only',
    title: 'Units only',
    hint: 'No weighing — unit-priced items only',
  },
];

export function WbTicketStartModal({
  open,
  onCancel,
  onStart,
}: WbTicketStartModalProps): JSX.Element {
  const yardsQ = useWbYards();
  const yards = yardsQ.data?.yards ?? [];

  const [direction, setDirection] = useState<TicketDirection | null>(null);
  const [weighingMode, setWeighingMode] = useState<WeighingMode | null>(null);
  const [yardEncId, setYardEncId] = useState<string | null>(null);
  const [weighbridgeEncId, setWeighbridgeEncId] = useState<string | null>(null);

  // Auto pre-select the yard when exactly one is available. Re-runs when
  // the yards list shifts (e.g. an admin adds a yard while the modal is
  // open) so the auto-pick stays sticky for single-yard tenants.
  useEffect(() => {
    if (yardEncId !== null) return;
    if (yards.length === 1) {
      setYardEncId(yards[0]!.enc_id);
    }
  }, [yards, yardEncId]);

  // Weighbridges scoped to the currently-selected yard. If no yard is
  // picked yet we pass `undefined` so the query stays effectively idle
  // (the server returns the unscoped list, but we hide the control until
  // a yard is chosen — see render below).
  const weighbridgesQ = useWbWeighbridges({
    yard_id: yardEncId ?? undefined,
  });
  const weighbridges = weighbridgesQ.data?.weighbridges ?? [];

  const isValid =
    direction !== null && weighingMode !== null && yardEncId !== null;

  function handleStart() {
    if (!isValid) return;
    const shell: PendingTicketShell = {
      direction: direction!,
      weighing_mode: weighingMode!,
      yard_enc_id: yardEncId!,
    };
    if (weighbridgeEncId !== null) {
      shell.weighbridge_enc_id = weighbridgeEncId;
    }
    onStart(shell);
  }

  function handleYardChange(next: string) {
    setYardEncId(next === '' ? null : next);
    // Reset the weighbridge — the previous bridge may not belong to the
    // newly-picked yard, and even if it does the list is about to refetch.
    setWeighbridgeEncId(null);
  }

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title="Start a new ticket"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleStart}
            disabled={!isValid}
            data-testid="wb-start-modal-start"
          >
            Start ticket
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        {/* -- Direction -------------------------------------------------- */}
        <div className={styles.section}>
          <span className={styles.sectionLabel}>
            Direction<span className={styles.required}>*</span>
          </span>
          <div className={styles.directionGrid}>
            {DIRECTION_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setDirection(opt.value)}
                className={cn(
                  styles.directionCard,
                  direction === opt.value && styles.directionCardSelected,
                )}
                aria-pressed={direction === opt.value}
                data-testid={`wb-start-direction-${opt.value}`}
              >
                <span className={styles.directionCardTitle}>{opt.title}</span>
                <span className={styles.directionCardHint}>{opt.hint}</span>
              </button>
            ))}
          </div>
        </div>

        {/* -- Weighing mode --------------------------------------------- */}
        <div className={styles.section}>
          <span className={styles.sectionLabel}>
            Weighing mode<span className={styles.required}>*</span>
          </span>
          <div className={styles.modeGrid}>
            {MODE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setWeighingMode(opt.value)}
                className={cn(
                  styles.modeCard,
                  weighingMode === opt.value && styles.modeCardSelected,
                )}
                aria-pressed={weighingMode === opt.value}
                data-testid={`wb-start-mode-${opt.value}`}
              >
                <span className={styles.modeCardTitle}>{opt.title}</span>
                <span className={styles.modeCardHint}>{opt.hint}</span>
              </button>
            ))}
          </div>
        </div>

        {/* -- Yard select ----------------------------------------------- */}
        <label className={styles.field}>
          <span>
            Yard<span className={styles.required}>*</span>
          </span>
          {yardsQ.isLoading ? (
            <Spinner label="Loading yards" size="sm" />
          ) : (
            <select
              value={yardEncId ?? ''}
              onChange={(e) => handleYardChange(e.target.value)}
              data-testid="wb-start-yard-select"
            >
              <option value="">Select a yard…</option>
              {yards.map((y) => (
                <option key={y.enc_id} value={y.enc_id}>
                  {y.name}
                </option>
              ))}
            </select>
          )}
        </label>

        {/* -- Weighbridge select (optional) ----------------------------- */}
        <label className={styles.field}>
          <span>Weighbridge</span>
          <select
            value={weighbridgeEncId ?? ''}
            disabled={yardEncId === null || weighbridgesQ.isLoading}
            onChange={(e) =>
              setWeighbridgeEncId(e.target.value === '' ? null : e.target.value)
            }
            data-testid="wb-start-weighbridge-select"
          >
            <option value="">
              {yardEncId === null ? 'Pick a yard first' : 'Optional — pick a weighbridge'}
            </option>
            {weighbridges.map((b) => (
              <option key={b.enc_id} value={b.enc_id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
      </div>
    </Modal>
  );
}
